import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2, LayoutGrid } from 'lucide-react';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Indice di tutte le categorie principali con le relative sottocategorie.
 * Sostituisce il vecchio link "Categorie" che portava per errore all'elenco artigiani.
 */
export default function CategoriesIndexPage() {
  const [rawCategories, setRawCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories/public`);
        if (res.ok) {
          const data = await res.json();
          if (active && data.categories?.length) {
            setRawCategories(data.categories);
            return;
          }
        }
        throw new Error('fallback');
      } catch (e) {
        const { data } = await supabase
          .from('categories')
          .select('id, name, slug, parent_id, icon, image_url, sort_order, seo_description')
          .or('is_active.eq.true,is_active.is.null');
        if (active) setRawCategories(data || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const mainCategories = useMemo(() => {
    if (!rawCategories.length) return [];
    return rawCategories
      .filter(c => !c.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map(main => ({
        ...main,
        subcategories: rawCategories
          .filter(c => c.parent_id === main.id)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
      }));
  }, [rawCategories]);

  return (
    <div className="min-h-screen bg-white" data-testid="categories-index-page">
      <Helmet>
        <title>Tutte le categorie di artigianato | Rarifex</title>
        <meta
          name="description"
          content="Esplora tutte le categorie di creazioni artigianali Made in Italy su Rarifex: moda, gioielli, arte, casa, eventi, bellezza e molto altro."
        />
        <link rel="canonical" href="https://rarifex.com/categorie" />
      </Helmet>

      {/* Hero */}
      <div className="border-b border-slate-100 bg-gradient-to-br from-[#008080]/6 via-white to-[#DAA520]/6">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-slate-200 px-3.5 py-1.5 mb-6">
            <LayoutGrid className="w-3.5 h-3.5 text-[#008080]" />
            <span className="text-xs font-semibold tracking-wide text-slate-600 uppercase">Categorie</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
            Tutto l'artigianato italiano,<br />
            <span className="text-[#008080]">categoria per categoria</span>
          </h1>
          <p className="text-base md:text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">
            Scegli il mondo che ti interessa e scopri le creazioni degli artigiani che lo abitano.
            Ogni categoria raccoglie pezzi unici, realizzati a mano e personalizzabili.
          </p>
        </div>
      </div>

      {/* Grid categorie */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
          </div>
        ) : mainCategories.length === 0 ? (
          <p className="text-slate-500 py-16 text-center">Nessuna categoria disponibile al momento.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {mainCategories.map((category, idx) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.06, 0.5), duration: 0.4 }}
              >
                <Link
                  to={`/categoria/${category.slug}`}
                  className="group block rounded-3xl overflow-hidden border border-slate-200 hover:border-[#008080]/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full bg-white"
                  data-testid={`category-card-${category.slug}`}
                >
                  <div className="relative h-44 overflow-hidden bg-slate-100">
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <LayoutGrid className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                    <h2 className="absolute bottom-4 left-5 right-5 text-white text-lg md:text-lg font-bold drop-shadow">
                      {category.name}
                    </h2>
                  </div>

                  <div className="p-5">
                    {category.subcategories.length > 0 && (
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.6rem]">
                        {category.subcategories.slice(0, 5).map(s => s.name).join(' · ')}
                        {category.subcategories.length > 5 ? ' e altro' : ''}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs text-slate-400">
                        {category.subcategories.length > 0
                          ? `${category.subcategories.length} sottocategorie`
                          : 'Scopri le creazioni'}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#008080] group-hover:gap-2.5 transition-all">
                        Esplora <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA finale */}
        <div className="mt-16 rounded-3xl bg-gradient-to-br from-[#008080] to-[#00615f] p-8 md:p-12 text-white text-center">
          <h2 className="text-lg md:text-lg font-bold mb-3">Non trovi quello che cerchi?</h2>
          <p className="text-sm md:text-base text-white/85 max-w-xl mx-auto leading-relaxed">
            Racconta la tua idea e lascia che siano gli artigiani a proporti la creazione perfetta,
            fatta su misura per te.
          </p>
          <Link
            to="/regalo-personalizzato"
            className="inline-flex items-center gap-2 mt-7 rounded-full bg-white text-[#00615f] px-7 py-3.5 font-semibold text-sm hover:bg-white/90 transition-colors"
            data-testid="categories-custom-cta"
          >
            Richiedi una creazione personalizzata <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
